import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useToastContext } from '../../../../../../../contexts'
import { useMode } from '../../../../../../../hooks/useMode'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import {
  CompanyCode,
  useAllCustomFieldDefinitions,
} from '../../../../suppliers'
import {
  requestCreateCompanyCode,
  requestUpdateCompanyCode,
  useAllCompanyCodes,
  useCompanyCodeById,
} from '../../../data'
import { FormValues } from '../common'
import { EditCompanyCodeForm } from './EditCompanyCode.form'

export const EditCompanyCodeData: React.FC = () => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { companyCodeId } = useParams()
  const navigate = useNavigate()
  const { data: companyCode } = useCompanyCodeById(companyCodeId, true)
  const { data: customFieldDefinitions } = useAllCustomFieldDefinitions()
  const { data: allCompanyCodes } = useAllCompanyCodes()
  const mode = useMode()

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const dtoFromFormData = (formData: FormValues): CompanyCode => {
    // Convert MasterDataSupplier to SupplierMasterDataRequest format
    return {
      IsStandard: formData.isStandard,
      Common: {
        DisplayId: formData.displayId,
        Company: formData.company,
        SalesTaxID: formData.salesTaxID,
        TaxNumber: formData.taxNumber,
      },
      Address: {
        Street: formData.street,
        PostalCode: formData.postalCode,
        City: formData.city,
        Country: formData.country,
      },
      CustomFields:
        formData.customFields?.map(customField => ({
          CustomFieldDefinition: customField.CustomFieldDefinition,
          Value: customField.Value.toString(),
        })) || [],
    }
  }

  const handleSubmit = async (formData: FormValues, resetForm: any) => {
    try {
      setIsRequestRunning(true)

      const dto = dtoFromFormData(formData)

      if (mode === 'add') await requestCreateCompanyCode(dto)
      if (mode === 'edit') await requestUpdateCompanyCode(companyCodeId!, dto)

      const summary =
        mode === 'add'
          ? t(
              'masterDataManagement.companyCodes.table.dialog.add.messages.success.summary',
            )
          : t(
              'masterDataManagement.companyCodes.table.dialog.save.messages.success.summary',
            )
      const detail =
        mode === 'add'
          ? t(
              'masterDataManagement.companyCodes.table.dialog.add.messages.success.detail',
              {
                company: formData?.company,
              },
            )
          : t(
              'masterDataManagement.companyCodes.table.dialog.save.messages.success.detail',
              {
                company: formData?.company,
              },
            )

      showToast({
        severity: 'success',
        summary: summary,
        detail: detail,
      })

      resetForm()

      handleClose()
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message
      const summary =
        mode === 'add'
          ? t(
              'masterDataManagement.companyCodes.table.dialog.add.messages.failure.summary',
            )
          : t(
              'masterDataManagement.companyCodes.table.dialog.save.messages.failure.summary',
            )

      showToast({
        severity: 'error',
        summary: summary,
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const handleClose = () => {
    navigate(-1)
  }

  const customFieldsOptions = customFieldDefinitions?.filter(
    def => def.Type === 'CompanyCode',
  )

  return (
    <EditCompanyCodeForm
      handleSubmit={handleSubmit}
      isRequestRunning={isRequestRunning}
      handleClose={handleClose}
      companyCode={companyCode}
      customFieldsOptions={customFieldsOptions}
      allCompanyCodes={allCompanyCodes}
    />
  )
}
